import IconButton from '@atlaskit/button/icon/button';

import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';

import CloseIcon from '@atlaskit/icon/core/cross';

import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';

import Lozenge from '@atlaskit/lozenge/lozenge';

import ButtonItem from '@atlaskit/menu/button-item';

import MenuGroup from '@atlaskit/menu/menu-group';

import Section from '@atlaskit/menu/section';

import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics/cross-flow/hooks';

// eslint-disable-next-line @atlaskit/platform/no-barrel-entry-imports -- Remote placements must use the host-shared Crossflow context.
import { type Options, useCrossFlow } from '@atlassiansox/cross-flow-support';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { FormattedMessage } from 'react-intl';

import { type PlacementRenderProps } from '../../index';

import { Nav4MenuItemBaseNoRef } from '../nav-4-menu-item';

import { type JSX } from 'react/jsx-runtime';

// We add some extra props that a Message using our pplacement might need.
// For example, we let users change a couple of styles by using a the 'type' string.
type ProductKey = 'jira-servicedesk.ondemand' | 'jira-product-discovery';

const CROSS_FLOW_SOURCE_COMPONENT = 'nav-4-requested-item';

export type MessageProps = {
	productName: 'Jira Product Discovery' | 'Jira Service Management';
	productKey: ProductKey;
	heading: {
		id:
			| 'cross-flow-product-request-pending.jpd.title'
			| 'cross-flow-product-request-pending.jsm.title';
		description: string;
		defaultMessage: string;
	};
};

// We export the props from the message along with the `dismiss` props from the placement.
export type Nav4RequestedItemProps = MessageProps & PlacementRenderProps;

// This is the component itself.
export const Nav4RequestedItem = ({
	dismiss,
	productKey,
	heading,
}: Nav4RequestedItemProps): JSX.Element => {
	// If you're curious about where details of your message, like the message template ID and
	//  message instance ID, come from, Post Office gives you all this info when it's time to
	// show the message. It uses a React Context Provider that's included in the message renderer
	// to do this.
	const crossFlow = useCrossFlow();

	const { fireInteractedUIEvent: fireCrossFlowInteractedUIEvent } =
		useFireCrossFlowAnalyticsEvents();

	const { onClosed, onCtaActioned } = usePostOfficeUserInteractions();

	const handleTry = () => {
		const crossFlowProps: Options = {
			targetProduct: productKey,
			sourceComponent: CROSS_FLOW_SOURCE_COMPONENT,
			sourceContext: 'nav-4-section',
		};
		void onCtaActioned({
			interactionIdentifier: 'requested',
			additionalMetadata: {},
		});

		//TEMP sourcename
		fireCrossFlowInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'listItem',
				actionSubjectId: 'product',
			},
			{
				source: 'atlassianSwitcherDiscoverProductsSection',
				targetProductId: productKey,
			},
		);

		if (crossFlow.isEnabled) {
			void crossFlow.api.open({
				...crossFlowProps,
			});
		}
	};

	const handleDismiss = () => {
		void onClosed({
			interactionIdentifier: 'dismiss',
			additionalMetadata: {},
		});

		//TODO sourcename
		fireCrossFlowInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'listItem',
				actionSubjectId: 'product',
			},
			{
				source: 'atlassianSwitcherDiscoverProductsSection',
				targetProductId: productKey,
			},
		);

		dismiss();
	};

	const icon = () => {
		if (productKey === 'jira-servicedesk.ondemand') {
			return (
				<svg
					width="20"
					height="20"
					viewBox="0 0 20 20"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M0 5C0 2.23858 2.23858 0 5 0H15C17.7614 0 20 2.23858 20 5V15C20 17.7614 17.7614 20 15 20H5C2.23858 20 0 17.7614 0 15V5Z"
						fill="#FFC716"
					/>
					<path
						d="M11.3955 8.52349H15.0577C15.6077 8.52349 15.7955 9.04667 15.4601 9.46252L9.73205 16.532C7.88083 15.0564 8.05522 12.7223 9.4101 11.0186L11.3955 8.52349ZM8.56498 11.3406H4.90278C4.35278 11.3406 4.16498 10.8174 4.50034 10.4015L10.2284 3.33203C12.0796 4.80764 11.8784 7.11496 10.5369 8.83203L8.56498 11.3406Z"
						fill="#101214"
					/>
				</svg>
			);
		}

		if (productKey === 'jira-product-discovery') {
			return (
				<svg
					width="20"
					height="20"
					viewBox="0 0 20 20"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M0 5C0 2.23858 2.23858 0 5 0H15C17.7614 0 20 2.23858 20 5V15C20 17.7614 17.7614 20 15 20H5C2.23858 20 0 17.7614 0 15V5Z"
						fill="#C97CF4"
					/>
					<path
						d="M11.2222 4.06816C12.7234 4.50854 14.2393 4.50854 15.7405 4.06816C15.873 4.0388 15.9613 4.12687 15.9318 4.25899C15.505 5.80031 15.505 7.22421 15.9318 8.76553C15.9613 8.88297 15.873 8.98572 15.7405 8.94169C14.2393 8.53067 12.7234 8.53067 11.2222 8.94169C11.1044 8.98572 11.0014 8.88297 11.0455 8.76553C11.4429 7.22421 11.4429 5.80031 11.0455 4.25899C11.0014 4.12687 11.1044 4.0388 11.2222 4.06816ZM11.8256 9.52886V9.77841C11.8256 10.9381 11.0161 11.7895 10.0153 11.7895C9.01451 11.7895 8.21975 10.9821 8.21975 9.98392C8.21975 8.98572 9.02922 8.19304 10.1625 8.19304H10.4568V4.68469C10.3097 4.67001 10.1625 4.67001 10.0153 4.67001C7.07177 4.67001 4.6875 7.04806 4.6875 9.9986C4.6875 12.9491 7.07177 15.3125 10.0153 15.3125C12.9589 15.3125 15.3578 12.9345 15.3578 9.9986V9.52886H11.8256Z"
						fill="#101214"
					/>
				</svg>
			);
		}

		return null;
	};

	return (
		<Nav4MenuItemBaseNoRef
			testId={`nav-4-requested-item-${productKey}`}
			elemBefore={icon()}
			elemAfter={
				<Lozenge appearance="inprogress">
					<FormattedMessage
						id="post-office.nav-4-requested-item.cta-button-lozenge-pending"
						defaultMessage="Pending"
						description="A lozenge to display product is pending"
					/>
				</Lozenge>
			}
			onClick={handleTry}
			actionsOnHover={
				<DropdownMenu
					shouldRenderToParent
					trigger={({ triggerRef, ...props }) => (
						<IconButton
							ref={triggerRef}
							{...props}
							spacing="compact"
							appearance="subtle"
							label="Open"
							icon={(iconProps) => <ShowMoreHorizontalIcon {...iconProps} size="small" />}
						/>
					)}
				>
					<MenuGroup minWidth="250px">
						<Section>
							<ButtonItem onClick={handleDismiss} iconBefore={<CloseIcon label="Close" />}>
								<FormattedMessage
									id="post-office.nav-4-requested-item.cta-button-dimissal"
									defaultMessage="Dismiss"
									description="A call to action to dismiss the request"
								/>
							</ButtonItem>
						</Section>
					</MenuGroup>
				</DropdownMenu>
			}
		>
			<FormattedMessage {...heading} />
		</Nav4MenuItemBaseNoRef>
	);
};
